import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "./utils";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Visual weight; maps onto `gz-button--primary` / `--quiet` / our danger modifier. @default "primary" */
  variant?: ButtonVariant;
  /** Control height scale (`--gz-control-size-*`). @default "md" */
  size?: ButtonSize;
  /** Shows `gz-spinner`, sets aria-busy and disables interaction. */
  loading?: boolean;
  /** Square icon-only button (`gz-button--icon`) — requires `aria-label`. */
  iconOnly?: boolean;
  /** Node rendered before the label, normally an `<Icon/>`. */
  leadingIcon?: ReactNode;
  /** Stretch to the full width of the parent. */
  block?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "gz-button--primary",
  secondary: "",
  ghost: "gz-button--quiet",
  danger: "xz-button--danger",
};

/** Design-system button (`gz-button`) plus our size/danger/loading modifiers. */
export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  iconOnly = false,
  leadingIcon,
  block = false,
  className,
  disabled,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        "gz-button",
        VARIANT_CLASS[variant],
        iconOnly && "gz-button--icon",
        `xz-button--${size}`,
        block && "xz-button--block",
        loading && "is-loading",
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className="gz-spinner" aria-hidden="true" />}
      {!loading && leadingIcon != null && <span className="xz-button__icon">{leadingIcon}</span>}
      {children != null && <span className="xz-button__label">{children}</span>}
    </button>
  );
}
